'use client';

import { type Key, type ReactNode, useEffect, useState } from 'react';

type VisibleCountConfig = {
  base: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
};

type CarouselState = {
  visibleCount: number;
  current: number;
  maxPosition: number;
  itemWidth: number;
  paginated: boolean;
  atStart: boolean;
  atEnd: boolean;
  previous: () => void;
  next: () => void;
  goToPosition: (position: number) => void;
};

type CarouselTrackRenderProps<T> = {
  item: T;
  index: number;
  tabIndex: number | undefined;
};

type CarouselTrackProps<T> = Pick<CarouselState, 'current' | 'itemWidth' | 'visibleCount'> & {
  items: readonly T[];
  getKey: (item: T, index: number) => Key;
  renderItem: (props: CarouselTrackRenderProps<T>) => ReactNode;
  className?: string;
  itemClassName?: string;
};

type CarouselArrowControlsProps = Pick<CarouselState, 'previous' | 'next' | 'atStart' | 'atEnd'> & {
  previousLabel: string;
  nextLabel: string;
  className?: string;
  previousIcon?: ReactNode;
  nextIcon?: ReactNode;
  buttonClassName?: (inactive: boolean) => string;
};

type CarouselDotsProps = Pick<CarouselState, 'current' | 'goToPosition'> & {
  count: number;
  labelPrefix: string;
  className?: string;
};

const BREAKPOINTS = [
  ['xl', '(min-width: 1280px)'],
  ['lg', '(min-width: 1024px)'],
  ['md', '(min-width: 768px)'],
  ['sm', '(min-width: 640px)'],
] as const;

function largestVisibleCount(config: VisibleCountConfig) {
  return config.xl ?? config.lg ?? config.md ?? config.sm ?? config.base;
}

function countForViewport(config: VisibleCountConfig) {
  for (const [key, query] of BREAKPOINTS) {
    if (config[key] && window.matchMedia(query).matches) return config[key];
  }
  return config.base;
}

function clamp(position: number, maxPosition: number) {
  return Math.max(0, Math.min(maxPosition, position));
}

export function useCarousel({
  itemCount,
  visibleCount: visibleCountConfig,
}: {
  itemCount: number;
  visibleCount: VisibleCountConfig;
}): CarouselState {
  const [visibleCount, setVisibleCount] = useState(() => largestVisibleCount(visibleCountConfig));
  const [position, setPosition] = useState(0);

  useEffect(() => {
    const update = () => setVisibleCount(countForViewport(visibleCountConfig));

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [visibleCountConfig]);

  const safeVisibleCount = Math.max(1, visibleCount);
  const maxPosition = Math.max(0, itemCount - safeVisibleCount);
  const current = clamp(position, maxPosition);
  const itemWidth = 100 / safeVisibleCount;
  const paginated = itemCount > safeVisibleCount;
  const atStart = current === 0;
  const atEnd = current === maxPosition;

  useEffect(() => {
    setPosition((p) => clamp(p, maxPosition));
  }, [maxPosition]);

  return {
    visibleCount: safeVisibleCount,
    current,
    maxPosition,
    itemWidth,
    paginated,
    atStart,
    atEnd,
    previous: () => setPosition((p) => clamp(clamp(p, maxPosition) - 1, maxPosition)),
    next: () => setPosition((p) => clamp(clamp(p, maxPosition) + 1, maxPosition)),
    goToPosition: (nextPosition) => setPosition(clamp(nextPosition, maxPosition)),
  };
}

export function CarouselTrack<T>({
  items,
  getKey,
  renderItem,
  current,
  itemWidth,
  visibleCount,
  className = '-mx-3 flex list-none p-0 transition-transform duration-300 ease-out',
  itemClassName = 'shrink-0 px-3',
}: CarouselTrackProps<T>) {
  return (
    <ul className={className} style={{ transform: `translateX(-${current * itemWidth}%)` }}>
      {items.map((item, index) => {
        const visible = index >= current && index < current + visibleCount;
        return (
          <li
            key={getKey(item, index)}
            className={itemClassName}
            style={{ flexBasis: `${itemWidth}%` }}
            aria-hidden={visible ? undefined : true}
          >
            {renderItem({ item, index, tabIndex: visible ? undefined : -1 })}
          </li>
        );
      })}
    </ul>
  );
}

export function CarouselArrowControls({
  previous,
  next,
  atStart,
  atEnd,
  previousLabel,
  nextLabel,
  className = 'flex items-center gap-2',
  previousIcon = <span aria-hidden>&larr;</span>,
  nextIcon = <span aria-hidden>&rarr;</span>,
  buttonClassName = (inactive) =>
    `flex size-10 items-center justify-center rounded-full border border-line text-ink transition ${
      inactive ? 'cursor-not-allowed opacity-40' : 'hover:bg-state-hover'
    }`,
}: CarouselArrowControlsProps) {
  return (
    <div className={className}>
      <button
        type="button"
        onClick={previous}
        aria-disabled={atStart}
        aria-label={previousLabel}
        className={buttonClassName(atStart)}
      >
        {previousIcon}
      </button>
      <button
        type="button"
        onClick={next}
        aria-disabled={atEnd}
        aria-label={nextLabel}
        className={buttonClassName(atEnd)}
      >
        {nextIcon}
      </button>
    </div>
  );
}

export function CarouselDots({
  count,
  current,
  goToPosition,
  labelPrefix,
  className = 'mt-8 flex justify-center gap-2',
}: CarouselDotsProps) {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => goToPosition(i)}
          aria-label={`${labelPrefix} ${i + 1}`}
          aria-current={i === current ? 'true' : undefined}
          className={`h-2 rounded-full transition-all ${
            i === current ? 'w-6 bg-ink' : 'w-2 bg-line hover:bg-line-strong'
          }`}
        />
      ))}
    </div>
  );
}
